import { useState, useEffect } from "react";
import { useApp } from "@/contexts/AppContext";
import api, { formatCurrency } from "@/lib/api";
import { Save, Brain, RefreshCw } from "lucide-react";

export default function PoliciesPage() {
  const { activeEntity, categories } = useApp();
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCat, setSelectedCat] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (activeEntity) params.entity_id = activeEntity.id;
        const { data } = await api.get("/policies", { params });
        setPolicies(data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
  }, [activeEntity]);

  const selectCategory = (cat) => {
    setSelectedCat(cat);
    const existing = policies.find(p => p.category_id === cat.id);
    if (existing) {
      setForm(existing);
    } else {
      setForm({
        category_id: cat.id, entity_id: activeEntity?.id || null,
        acc_method: "STRAIGHT_LINE", acc_useful_life_months: 60, acc_residual_pct: 5,
        acc_mid_month_convention: false,
        tax_method: "STRAIGHT_LINE", tax_writeoff_years: 5, tax_schedule: {},
        tax_residual_pct: 0, disclosure_narrative: ""
      });
    }
    setAiSuggestion(null);
  };

  const savePolicy = async () => {
    setSaving(true);
    try {
      await api.post("/policies", form);
      alert("Policy saved");
      const params = {};
      if (activeEntity) params.entity_id = activeEntity.id;
      const { data } = await api.get("/policies", { params });
      setPolicies(data);
    } catch (e) { alert(e.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const getAiSuggestion = async () => {
    if (!selectedCat) return;
    setAiLoading(true);
    try {
      const { data } = await api.post("/ai/policy-suggest", {
        category_id: selectedCat.id, entity_id: activeEntity?.id
      });
      setAiSuggestion(data);
    } catch (e) { console.error(e); }
    finally { setAiLoading(false); }
  };

  const applyAiSuggestion = () => {
    if (!aiSuggestion) return;
    setForm({
      ...form,
      acc_method: aiSuggestion.acc_method || form.acc_method,
      acc_useful_life_months: aiSuggestion.acc_useful_life_months || form.acc_useful_life_months,
      acc_residual_pct: aiSuggestion.acc_residual_pct ?? form.acc_residual_pct,
      tax_method: aiSuggestion.tax_method || form.tax_method,
      tax_writeoff_years: aiSuggestion.tax_writeoff_years || form.tax_writeoff_years,
    });
  };

  return (
    <div className="space-y-6" data-testid="policies-page">
      <div>
        <h1 className="font-display text-2xl font-semibold text-[#0B1020]">Category Policies</h1>
        <p className="text-sm text-[#6B7280] mt-1">Accounting and tax policy settings per asset category</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Category List */}
        <div className="stratum-card overflow-hidden">
          <div className="p-3 border-b border-[#E8E2D5] bg-[#FAF8F4]">
            <span className="text-xs font-medium text-[#6B7280] uppercase tracking-wider">Categories</span>
          </div>
          <div className="divide-y divide-[#E8E2D5]">
            {categories.map(cat => {
              const hasPolicy = policies.some(p => p.category_id === cat.id);
              return (
                <button
                  key={cat.id}
                  onClick={() => selectCategory(cat)}
                  className={`w-full text-left px-4 py-3 hover:bg-[#FAF8F4] transition-colors ${selectedCat?.id === cat.id ? "bg-[#FAF8F4] border-l-2 border-l-[#C8A961]" : ""}`}
                  data-testid={`policy-cat-${cat.id}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#1A1A1A]">{cat.name}</span>
                    {hasPolicy && <span className="w-2 h-2 rounded-full bg-[#065F46]" title="Policy set" />}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Policy Form */}
        <div className="lg:col-span-2 space-y-4">
          {selectedCat ? (
            <>
              {/* AI Suggestion Card */}
              <div className="stratum-card p-5 border-l-4 border-l-[#C8A961]">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Brain size={16} className="text-[#C8A961]" />
                    <span className="text-sm font-medium text-[#0B1020]">Stratum Recommends</span>
                  </div>
                  <button onClick={getAiSuggestion} disabled={aiLoading} className="px-3 py-1.5 text-xs rounded-lg border border-[#E8E2D5] hover:bg-[#FAF8F4] flex items-center gap-1.5" data-testid="ai-suggest-btn">
                    <RefreshCw size={12} className={aiLoading ? "animate-spin" : ""} />
                    {aiLoading ? "Analysing..." : "Get AI Suggestion"}
                  </button>
                </div>
                {aiSuggestion && (
                  <div className="mt-3 p-3 rounded-lg bg-[#FAF8F4] text-sm">
                    <p className="text-[#1A1A1A] mb-2">{aiSuggestion.reasoning}</p>
                    <div className="flex gap-4 text-xs text-[#6B7280] mb-3">
                      <span>Method: <strong>{aiSuggestion.acc_method}</strong></span>
                      <span>Life: <strong>{aiSuggestion.acc_useful_life_months} months</strong></span>
                      <span>Residual: <strong>{aiSuggestion.acc_residual_pct}%</strong></span>
                    </div>
                    <button onClick={applyAiSuggestion} className="px-3 py-1.5 text-xs rounded-lg bg-[#C8A961] text-white hover:bg-[#B69A51]" data-testid="apply-ai-btn">Apply Suggestion</button>
                  </div>
                )}
              </div>

              {/* Accounting Policy */}
              <div className="stratum-card p-6">
                <h3 className="font-display text-lg font-semibold mb-4">{selectedCat.name} - Accounting Policy</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Depreciation Method</label>
                    <select className="stratum-input w-full" value={form.acc_method} onChange={(e) => setForm({...form, acc_method: e.target.value})} data-testid="policy-method-select">
                      <option value="STRAIGHT_LINE">Straight-line</option>
                      <option value="DIMINISHING">Diminishing balance</option>
                      <option value="UOP">Units of production</option>
                      <option value="SOYD">Sum-of-years-digits</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Useful Life (months)</label>
                    <input type="number" className="stratum-input w-full font-mono" value={form.acc_useful_life_months} onChange={(e) => setForm({...form, acc_useful_life_months: parseInt(e.target.value)||0})} data-testid="policy-life-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Residual Value %</label>
                    <input type="number" className="stratum-input w-full font-mono" step="0.1" value={form.acc_residual_pct} onChange={(e) => setForm({...form, acc_residual_pct: parseFloat(e.target.value)||0})} data-testid="policy-residual-input" />
                  </div>
                  <div className="flex items-center gap-3 pt-5">
                    <input type="checkbox" checked={form.acc_mid_month_convention} onChange={(e) => setForm({...form, acc_mid_month_convention: e.target.checked})} className="rounded" data-testid="policy-midmonth-checkbox" />
                    <span className="text-sm">Mid-month convention</span>
                  </div>
                </div>
              </div>

              {/* Tax Policy */}
              <div className="stratum-card p-6">
                <h3 className="font-display text-lg font-semibold mb-4">Tax Policy (SARS)</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Tax Method</label>
                    <select className="stratum-input w-full" value={form.tax_method} onChange={(e) => setForm({...form, tax_method: e.target.value})} data-testid="policy-tax-method-select">
                      <option value="STRAIGHT_LINE">Straight-line</option>
                      <option value="WT_SCHEDULE">Wear & Tear Schedule</option>
                      <option value="S12C">S12C (Manufacturing)</option>
                      <option value="S12E">S12E (Small Business)</option>
                      <option value="S11E">S11(e)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Write-off Period (years)</label>
                    <input type="number" className="stratum-input w-full font-mono" value={form.tax_writeoff_years} onChange={(e) => setForm({...form, tax_writeoff_years: parseInt(e.target.value)||0})} data-testid="policy-tax-years-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-[#6B7280] mb-1">Tax Residual %</label>
                    <input type="number" className="stratum-input w-full font-mono" step="0.1" value={form.tax_residual_pct} onChange={(e) => setForm({...form, tax_residual_pct: parseFloat(e.target.value)||0})} data-testid="policy-tax-residual-input" />
                  </div>
                </div>
              </div>

              {/* Disclosure */}
              <div className="stratum-card p-6">
                <h3 className="font-display text-lg font-semibold mb-4">Disclosure Narrative</h3>
                <textarea className="stratum-input w-full" rows={4} value={form.disclosure_narrative || ""} onChange={(e) => setForm({...form, disclosure_narrative: e.target.value})} placeholder="Free-text rationale for AFS policy document..." data-testid="policy-narrative-input" />
              </div>

              <div className="flex justify-end">
                <button onClick={savePolicy} disabled={saving} className="stratum-btn-primary flex items-center gap-2 disabled:opacity-50" data-testid="save-policy-btn">
                  <Save size={16} /> {saving ? "Saving..." : "Save Policy"}
                </button>
              </div>
            </>
          ) : (
            <div className="stratum-card p-12 text-center text-[#6B7280]">
              <p>Select a category from the list to view and edit its policy settings.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
